import type { GeneralReportRow } from './types';
import type { Book, Project, UsedBank } from 'library/types';
import { normalizeProjectCompanyCodes } from 'utils/product-company-codes';
import type { BOOK_BANK_STATUS_TYPE_ENUM, PRODUCT_COMPANY_STATUS_TYPE_ENUM } from 'enums';
import { flattenProductBankAssignments, type ProductBankAssignment } from 'utils/product-banks';

type IndexedBankAssignment = {
  assignment: ProductBankAssignment;
  duplicateOrdinal: number;
};

type JoinedCompany = {
  companyCode: string;
  companyStatusByProduct: PRODUCT_COMPANY_STATUS_TYPE_ENUM | null;
};

const toArray = <T>(value: readonly T[] | null | undefined): readonly T[] => (Array.isArray(value) ? value : []);

const normalizeJoinCompanyCode = (value: unknown): string => String(value ?? '').trim();

const createRowId = (
  bookId: number,
  bookOccurrence: number,
  projectId: number,
  projectOccurrence: number,
  companyCode: string,
  bankId: number | null,
  bankStatusByProduct: BOOK_BANK_STATUS_TYPE_ENUM | null,
  duplicateOrdinal: number
): string =>
  [
    'general-report',
    `book-${bookId}-${bookOccurrence}`,
    `project-${projectId}-${projectOccurrence}`,
    `company-${encodeURIComponent(companyCode)}`,
    `bank-${bankId ?? 'no-bank'}`,
    `bank-status-${bankStatusByProduct ?? 'no-bank-status'}`,
    `duplicate-${duplicateOrdinal}`,
  ].join(':');

export const buildGeneralReportRows = (
  books: readonly Book[],
  projects: readonly Project[],
  usedBanks: readonly UsedBank[]
): GeneralReportRow[] => {
  const projectTitleById = new Map(projects.map((project) => [project.id, project.title]));
  const bankTitleById = new Map(usedBanks.map((bank) => [bank.id, bank.title]));
  const bookOccurrences = new Map<number, number>();

  return books.flatMap((book) => {
    const bookOccurrence = bookOccurrences.get(book.id) ?? 0;
    bookOccurrences.set(book.id, bookOccurrence + 1);

    const companyTitleByCode = new Map(
      toArray(book.companies).map((company) => [normalizeJoinCompanyCode(company.code), company.title])
    );
    const projectOccurrences = new Map<number, number>();

    return toArray(book.projects).flatMap((bookProject) => {
      const projectOccurrence = projectOccurrences.get(bookProject.id) ?? 0;
      projectOccurrences.set(bookProject.id, projectOccurrence + 1);

      const selectedCompanyCodes = normalizeProjectCompanyCodes(bookProject.companyCodes);
      const bankGroups = toArray(bookProject.banks);
      const assignmentsByCompanyCode = new Map<string, IndexedBankAssignment[]>();
      const duplicateCountsByRelation = new Map<string, number>();

      flattenProductBankAssignments(bankGroups).forEach((assignment) => {
        const companyCode = normalizeJoinCompanyCode(assignment.companyCode);
        const relationKey = JSON.stringify([companyCode, assignment.bankId, assignment.status]);
        const duplicateOrdinal = duplicateCountsByRelation.get(relationKey) ?? 0;

        duplicateCountsByRelation.set(relationKey, duplicateOrdinal + 1);

        const indexedAssignment: IndexedBankAssignment = {
          assignment: { ...assignment, companyCode },
          duplicateOrdinal,
        };
        const assignments = assignmentsByCompanyCode.get(companyCode);

        if (assignments) {
          assignments.push(indexedAssignment);
        } else {
          assignmentsByCompanyCode.set(companyCode, [indexedAssignment]);
        }
      });

      const joinedCompanies: JoinedCompany[] = [];
      const includedCompanyCodes = new Set<string>();

      selectedCompanyCodes.forEach(({ companyCode, status }) => {
        const normalizedCompanyCode = normalizeJoinCompanyCode(companyCode);

        includedCompanyCodes.add(normalizedCompanyCode);
        joinedCompanies.push({
          companyCode: normalizedCompanyCode,
          companyStatusByProduct: status,
        });
      });

      bankGroups.forEach((group) => {
        const companyCode = normalizeJoinCompanyCode(group.companyCode);

        if (includedCompanyCodes.has(companyCode)) return;

        includedCompanyCodes.add(companyCode);
        joinedCompanies.push({
          companyCode,
          companyStatusByProduct: null,
        });
      });

      if (joinedCompanies.length === 0) {
        joinedCompanies.push({
          companyCode: '',
          companyStatusByProduct: null,
        });
      }

      return joinedCompanies.flatMap<GeneralReportRow>(({ companyCode, companyStatusByProduct }) => {
        const indexedAssignments = assignmentsByCompanyCode.get(companyCode) ?? [];
        const commonFields = {
          bookId: book.id,
          projectId: bookProject.id,
          book: book.companyName?.trim() || `#${book.id}`,
          project: projectTitleById.get(bookProject.id) ?? `#${bookProject.id}`,
          companyCode,
          companyTitle: companyTitleByCode.get(companyCode) ?? '',
          companyStatusByProduct,
        };

        if (indexedAssignments.length === 0) {
          return [
            {
              ...commonFields,
              id: createRowId(book.id, bookOccurrence, bookProject.id, projectOccurrence, companyCode, null, null, 0),
              bankId: null,
              bankName: null,
              bankStatusByProduct: null,
            },
          ];
        }

        return indexedAssignments.map(({ assignment, duplicateOrdinal }) => ({
          ...commonFields,
          id: createRowId(
            book.id,
            bookOccurrence,
            bookProject.id,
            projectOccurrence,
            companyCode,
            assignment.bankId,
            assignment.status,
            duplicateOrdinal
          ),
          bankId: assignment.bankId,
          bankName: bankTitleById.get(assignment.bankId) ?? `#${assignment.bankId}`,
          bankStatusByProduct: assignment.status,
        }));
      });
    });
  });
};
